import type { ShapeName } from '../core/types';

/** DOM 조립 헬퍼. 프레임워크 없이 최소한만 쓴다. */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (html !== undefined) n.innerHTML = html;
  return n;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/** 도형을 SVG 경로로. 캔버스 쪽 도형과 같은 모양을 쓴다. */
function shapePoints(shape: ShapeName, r: number): string {
  const c = r;
  const pts: number[][] = [];
  switch (shape) {
    case 'square':
      return `M${c - r * 0.82} ${c - r * 0.82}h${r * 1.64}v${r * 1.64}h${-r * 1.64}z`;
    case 'triangle':
      pts.push([c, c - r], [c + r * 0.92, c + r * 0.75], [c - r * 0.92, c + r * 0.75]);
      break;
    case 'diamond':
      pts.push([c, c - r], [c + r * 0.78, c], [c, c + r], [c - r * 0.78, c]);
      break;
    case 'hexagon':
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        pts.push([c + Math.cos(a) * r, c + Math.sin(a) * r]);
      }
      break;
    case 'star':
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const rr = i % 2 === 0 ? r : r * 0.45;
        pts.push([c + Math.cos(a) * rr, c + Math.sin(a) * rr]);
      }
      break;
    case 'cross': {
      const t = r * 0.34;
      pts.push(
        [c - t, c - r], [c + t, c - r], [c + t, c - t], [c + r, c - t],
        [c + r, c + t], [c + t, c + t], [c + t, c + r], [c - t, c + r],
        [c - t, c + t], [c - r, c + t], [c - r, c - t], [c - t, c - t],
      );
      break;
    }
    case 'circle':
    default:
      return '';
  }
  return `M${pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L')}Z`;
}

/** 아이콘 HTML. sprite 경로가 있으면 이미지를, 없으면 도형 SVG를 낸다. */
export function iconHtml(
  shape: string,
  color: string,
  sprite: string | undefined,
  size = 22,
): string {
  if (sprite && sprite.trim() !== '') {
    return `<img class="icon" src="${escapeHtml(sprite)}" width="${size}" height="${size}" alt="" />`;
  }
  const r = size / 2;
  const s = shape as ShapeName;
  const body =
    s === 'circle' || !s
      ? `<circle cx="${r}" cy="${r}" r="${r}" fill="${escapeHtml(color)}"/>`
      : `<path d="${shapePoints(s, r)}" fill="${escapeHtml(color)}"/>`;
  return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${body}</svg>`;
}

/** 초를 m:ss 로. */
export function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
